// The three facts the execution table needed and the payload did not carry as fields.
//
// Every one of these is computed here rather than added to `book.py`, and that is a deliberate
// line rather than laziness: each is a *reading* of data already on the row, not a new fact
// about the account. `reachableContacts` is a filter over `contacts`, `lastOutbound` is the
// first outbound entry in `timeline`, and `whyNow` is a priority order over fields that all
// already exist. Pushing any of them server-side would mean a Snowflake change and a
// twice-daily rebuild to alter something that is really a presentation decision.
//
// One thing genuinely does depend on the payload, and it is worth knowing: `lastOutbound` reads
// `timeline`, which the server caps at 25 touches per account. The cap cannot make this wrong --
// the query orders by recency, so the most recent touch is always present -- but anything that
// wanted an *older* touch out of this array would need the cap raised.

import { pacificCalendarDay } from '../lib/format.ts';
import type { AccountScore, BookAccount, CompanyFunding, Contact, RankingPayload, TimelineEntry } from '../api/types';
import {
  claySignalIsFresh, formatSeenDay, freshnessStamp, isHiddenSignal, signalPhrase,
} from './signalCopy.ts';

// Somebody we can actually write to. The three hard stops are the same ones
// `pushBlockedReason` enforces, because a contact the push would refuse is not a way in.
// Null is unknown, not a hard stop — a pending Scout save has not been described by the
// mirror yet, and treating that as opted-out would hide the Reachable count this column is for.
export function isReachable(contact: Contact): boolean {
  if (contact.opted_out || contact.bounced || contact.amplemarket_excluded) return false;
  return Boolean(contact.email || contact.linkedin);
}

export function reachableContacts(row: BookAccount): Contact[] {
  return row.contacts.filter(isReachable);
}

// Salesforce writes the email address into `NAME` when it holds no real name -- the same field
// that carries "[placeholder]", which `_real_name_or_none` already strips server-side. An
// address is not a name: in a fixed-width cell it truncates to something unreadable, and it
// tells a strategist nothing they could not get from the panel.
function displayableName(name: string | null): string | null {
  if (!name) return null;
  return name.includes('@') ? null : name;
}

export interface LastOutbound {
  day: string;
  // Which person we wrote to. Null on touches Salesforce logged against the company with
  // nobody attached, which is a real state rather than a gap.
  who: string | null;
  // Which of our reps sent it.
  author: string | null;
  // Whether that rep is somebody other than the account's owner. This is the collision
  // warning, kept alive after the "Also worked by" column folded into this one.
  byOther: boolean;
}

// The most recent outbound touch on this account, whoever made it.
//
// Deliberately not the owner's most recent touch. The question the column answers is "who was
// the last person from Cartesia in here", and answering it with the owner's own last email
// would hide exactly the case worth seeing -- a colleague already in a live conversation.
export function lastOutbound(row: BookAccount): LastOutbound | null {
  const sent = row.timeline.find((entry: TimelineEntry) => entry.direction === 'sent');
  if (sent) {
    return {
      day: sent.day,
      who: displayableName(sent.who),
      author: sent.author,
      byOther: Boolean(sent.author && sent.author !== row.sdr_owner),
    };
  }

  // The timeline holds no outbound row but the counts say the owner has worked this account.
  // The two come from different queries, so they can disagree -- and when they do, "never"
  // beside "9 sent" is the one output that is definitely wrong. Fall back to the owner's own
  // last touch, which is aggregated over the full history and cannot be missing.
  if (row.touches && row.last_touch) {
    return { day: row.last_touch, who: null, author: row.sdr_owner, byOther: false };
  }
  return null;
}

// --- Why now ---------------------------------------------------------------------------------
// The strongest reason to act on this row today, as one chip and one line.
//
// A waterfall rather than a Signal column, because a Signal column is empty on 98% of the book:
// Clay has written an intent signal on roughly two accounts in a hundred. Ranking the reasons
// means the column is never dead, and it gets *stronger* rather than being replaced as signals
// arrive -- the signal simply starts winning more often.
//
// The order is by how much each reason should change what somebody does this morning, which is
// not the same as how interesting it is. An intent signal is a reason to write today. A
// company that stopped burning credits is a reason to write today. A tier band is a reason this
// account is on the book at all, which is weaker, so it sits near the bottom.
//
// **A shared Slack channel is deliberately not in here.** It is already a mark on the account
// name, and it is a reason *not* to send something cold -- putting it in a column headed "why
// now" would read as encouragement.

export type WhyNowTone = 'signal' | 'warn' | 'neutral';

export interface WhyNow {
  // The chip's text. Short enough for a table cell; the detail line carries the rest.
  label: string;
  detail: string | null;
  tone: WhyNowTone;
  // What kind of reason this is, so the column can be sorted and filtered on it. Ranked in
  // `WHY_NOW_RANK` below rather than sorted alphabetically, for the same reason the plan
  // column ranks tiers numerically.
  kind: 'signal' | 'usage_stopped' | 'usage_climbing' | 'overage' | 'tier' | 'note' | 'quiet';
}

// Enough of a rise to be worth acting on, and enough volume that the percentage means
// something. A 400% rise on four credits is a rounding error with a chip attached.
const CLIMB_PERCENT = 25;
const CLIMB_FLOOR_CREDITS = 500;
// Usage minimums, the same as rank_books (GRO-259): a stop needs a real prior period, and
// a climb off a near-zero prior is not a climb (+125,693% on 47 credits).
const USAGE_STOPPED_MIN_PRIOR = 1000;
const CLIMB_MIN_PRIOR = 1000;

export const WHY_NOW_RANK: Record<WhyNow['kind'], number> = {
  signal: 6, usage_stopped: 5, usage_climbing: 4, overage: 3, tier: 2, note: 1, quiet: 0,
};

export function whyNow(row: BookAccount, today?: string): WhyNow | null {
  // 1. What Clay found, when it is fresh. An old, undated, or hidden name is leftover
  //    and must not outrank usage or tier — same gate as rank_books.choose_tag.
  const signal = row.clay?.signal;
  const bookToday = today || bookTodayOf(row);
  if (signal?.name && claySignalIsFresh(signal, bookToday)) {
    return {
      label: signalPhrase(signal.name),
      detail: formatSeenDay(freshnessStamp(signal)),
      tone: 'signal',
      kind: 'signal',
    };
  }

  // 2. They were burning credits and stopped. The churn read that was invisible until credits
  //    were recomputed from the product mirror -- Salesforce's own rollups report zero on
  //    hundreds of orgs where real usage exists, so this chip could not previously be trusted.
  if (!row.credits_30 && (row.credits_prior_30 ?? 0) >= USAGE_STOPPED_MIN_PRIOR) {
    return {
      label: 'usage stopped',
      detail: `${Math.round(row.credits_prior_30 as number).toLocaleString()} → 0 credits`,
      tone: 'warn',
      kind: 'usage_stopped',
    };
  }

  // 3. Usage climbing, on enough volume for the percentage to mean something.
  if ((row.credits_trend_pct ?? 0) >= CLIMB_PERCENT
      && (row.credits_30 ?? 0) >= CLIMB_FLOOR_CREDITS
      && (row.credits_prior_30 ?? 0) >= CLIMB_MIN_PRIOR) {
    return {
      label: `+${Math.round(row.credits_trend_pct as number)}% credits`,
      detail: `${Math.round(row.credits_30 as number).toLocaleString()} in 30 days`,
      tone: 'signal',
      kind: 'usage_climbing',
    };
  }

  // 4. An org over its plan. Carried per org since the usage rollup landed and shown nowhere
  //    on the table until now.
  if (row.orgs.some(org => org.overage)) {
    return { label: 'overage', detail: 'an org is over its plan', tone: 'warn',
             kind: 'overage' };
  }

  // 5. The OB Engine's band. Weaker than everything above -- it says why this account is on
  //    the book, not why today -- but it is populated wherever the engine has run, which is
  //    where most rows will land until signals arrive at volume.
  const engine = row.clay?.engine;
  if (engine?.tier_label || engine?.tier != null) {
    const band = engine.tier_label || `Tier ${engine.tier}`;
    return { label: band, detail: engine.tier_reason || 'OB Engine · no signal yet',
             tone: 'neutral', kind: 'tier' };
  }

  // 6. Somebody wrote something. Last because it is the least structured -- and first in the
  //    panel, where it can be disqualifying.
  if (row.clay?.human_notes) {
    return { label: 'team note', detail: 'a colleague left a note', tone: 'neutral',
             kind: 'note' };
  }

  return null;
}


// --- Scout's ranking, joined by account id --------------------------------------------------
// A second payload, never spliced into the book. Rebuilds run at 02:15 and 14:15 Pacific,
// twelve hours apart. A ranking older than the book by more than one of those cycles is last
// night's order sitting on this afternoon's book, and the waterfall is more honest.

export const ONE_REBUILD_MS = 12 * 60 * 60 * 1000;

export interface RankedFields {
  ranking: number | null;
  tag: WhyNow | null;
  work_reason?: WorkReason | null;
}

// A shallow copy of a book row plus the ranking join. The cached book is never
// mutated; ExecutionView maps through this before the table or the CSV see a row.
export type RankedAccount = BookAccount & RankedFields & {
  // Presentation only. sort.ts keeps lower `group` above higher, in every sort state:
  // pin (0), body (1), auto (2), booked (3), hide (4).
  board_pref?: 'pinned' | 'hidden' | null;
  group?: number;
  // The book's Pacific calendar day, so whyNow ages signals the same way rank_books does.
  book_today?: string;
  // Apollo funding and investor ties, joined by `book/funding.ts` before the ranking (GRO-159).
  funding?: CompanyFunding | null;
  // Fit and Engagement, joined by `book/scores.ts` when scores are visible (GRO-381).
  score?: AccountScore | null;
};

function bookTodayOf(row: BookAccount): string {
  const extra = row as BookAccount & { book_today?: string };
  return extra.book_today || '';
}

export function attachRanking(
  rows: BookAccount[],
  rankingPayload: RankingPayload | null,
  bookGeneratedAt: string,
  bookToday: string = '',
): RankedAccount[] {
  return rows.map(row => {
    const fields = rankedFields(row, rankingPayload, bookGeneratedAt, bookToday);
    return {
      ...row,
      ...fields,
      book_today: bookToday,
      work_reason: resolveWorkReason(row, rankingPayload, bookGeneratedAt, bookToday),
    };
  });
}

function toneForKind(kind: WhyNow['kind']): WhyNowTone {
  if (kind === 'signal' || kind === 'usage_climbing') return 'signal';
  if (kind === 'usage_stopped' || kind === 'overage') return 'warn';
  return 'neutral';
}

export function rankingIsStale(
  rankingComputedAt: string | null | undefined,
  bookGeneratedAt: string,
): boolean {
  if (!rankingComputedAt) return true;
  const rankingTime = Date.parse(rankingComputedAt);
  const bookTime = Date.parse(bookGeneratedAt);
  if (Number.isNaN(rankingTime) || Number.isNaN(bookTime)) return true;
  return bookTime - rankingTime > ONE_REBUILD_MS;
}

export function rankedFields(
  row: BookAccount,
  rankingPayload: RankingPayload | null,
  bookGeneratedAt: string,
  bookToday: string = '',
): RankedFields {
  const waterfall = whyNow(row, bookToday);
  if (!rankingPayload || rankingIsStale(rankingPayload.computed_at, bookGeneratedAt)) {
    return { ranking: null, tag: waterfall };
  }

  const entry = rankingPayload.by_account_id[row.id];
  if (!entry) {
    return { ranking: null, tag: waterfall };
  }

  return {
    ranking: entry.rank,
    tag: {
      label: entry.tag_text,
      detail: entry.reason || null,
      tone: toneForKind(entry.tag_kind),
      kind: entry.tag_kind,
    },
  };
}


export interface WorkReason extends WhyNow {
  source: 'scout' | 'book';
  computed_at: string | null;
  observed_at: string | null;
}

// One presentation decision for the table, filters, export and account panel.
// Preserve the existing batch freshness policy; snapshot versioning is a separate change.
export function resolveWorkReason(
  row: BookAccount,
  rankingPayload: RankingPayload | null,
  bookGeneratedAt: string,
  bookToday: string = '',
): WorkReason | null {
  const fields = rankedFields(row, rankingPayload, bookGeneratedAt, bookToday);
  if (!fields.tag) return null;
  const entry = fields.ranking == null ? null : rankingPayload?.by_account_id[row.id];
  const signal = row.clay?.signal;
  const matchesSignal = fields.tag.kind === 'signal' && !isHiddenSignal(signal?.name)
    && signal?.name?.trim().toLowerCase() === fields.tag.label.trim().toLowerCase();
  return {
    ...fields.tag,
    source: entry ? 'scout' : 'book',
    computed_at: entry?.computed_at || (entry ? rankingPayload?.computed_at : bookGeneratedAt) || null,
    observed_at: matchesSignal ? freshnessStamp(signal || {}) || null : null,
  };
}

export function workReasonSource(reason: WorkReason): string {
  const source = reason.source === 'scout' ? 'Scout ranking' : 'Book snapshot';
  const computed = reason.computed_at ? pacificCalendarDay(reason.computed_at) : '';
  const observed = reason.observed_at ? `Seen ${pacificCalendarDay(reason.observed_at)} · ` : '';
  return observed + source + (computed ? ` · ${computed}` : '');
}

// --- A deal we already lost here ------------------------------------------------------------
// The second flag on the account name, beside shared Slack, and for a related reason: both say
// "this is not a cold account, read the history before you write".
//
// A closed-lost deal is the more common of the two -- 25 of the book's 422 accounts carry an
// opportunity and 30 of the 31 deals on it are closed -- and it changes the opening rather than
// forbidding it. Somebody who evaluated us and said no last quarter is a different conversation
// from a stranger, and the month is what tells a strategist which.

export interface LostDeal {
  // "08/26". The month rather than the date: what matters is how long ago, and a precise day
  // implies a precision about *why* that the record does not carry. Numeric rather than
  // spelled out because this sits in a fixed-width flag beside another one, and "Aug 2026" is
  // half again as wide as the thing it is lined up against.
  when: string;
  monthsAgo: number;
}

// Deals older than this stop being a live consideration and stop being flagged. Two years is
// long enough to cover a full budget cycle and short enough that the chip still means something
// when it appears.
const LOST_DEAL_MONTHS = 24;

export function recentLostDeal(row: BookAccount, today: Date): LostDeal | null {
  let best: LostDeal | null = null;

  for (const deal of row.opportunities) {
    // Closed and not won. An open deal is not a warning, and a won one is an account team's
    // relationship rather than something to flag on a prospecting board.
    if (!deal.closed || deal.won) continue;
    const day = deal.close_date || deal.created;
    if (!day) continue;

    const closedAt = new Date(day + 'T00:00:00Z');
    const monthsAgo = Math.round(
      (today.getTime() - closedAt.getTime()) / (864e5 * 30.44));
    if (monthsAgo > LOST_DEAL_MONTHS || monthsAgo < 0) continue;

    // The most recent loss wins. An account that lost twice is described by the later one.
    if (best && best.monthsAgo <= monthsAgo) continue;
    const month = String(closedAt.getUTCMonth() + 1).padStart(2, '0');
    const year = String(closedAt.getUTCFullYear()).slice(2);
    best = { when: `${month}/${year}`, monthsAgo };
  }
  return best;
}
